import React from 'react';

import { Button } from '@/components/design-system/Button';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import ShiplexLogo from '@/components/ui/ShiplexLogo';
import { ROUTES } from '@/consts/routes';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { EmailRequestForm } from './components/EmailRequestForm';
import { PasswordResetFooter } from './components/PasswordResetFooter';
import { VerificationCodeForm } from './components/VerificationCodeForm';
import { usePasswordReset } from './hooks/usePasswordReset';

export const PasswordResetScreen: React.FC = () => {
  const { t } = useTranslation();
  const { step, email, error, loading, handleEmailSubmit, handleCodeSubmit, handleReenterEmail } = usePasswordReset();

  if (step === 'email') {
    return (
      <div className="flex h-screen w-full scale-85 flex-col items-center justify-center 2xl:scale-100">
        <ShiplexLogo color="duotoneDark" className="h-10 w-auto" />
        <h3 className="pt-10 text-black/65">{t('passwordReset.title')}</h3>
        <p className="text-typography-900/45 my-2 mb-6 w-81 text-center">{t('passwordReset.message')}</p>

        <EmailRequestForm onSubmit={handleEmailSubmit} loading={loading} initialEmail={email} error={error} />

        <div className="flex w-81 justify-end">
          <Link to={ROUTES.auth.login} className="text-primary hover:text-primary/75 mt-3 transition-all duration-300">
            {t('passwordReset.back')}
          </Link>
        </div>

        <ErrorBanner error={error} className="mt-3" />
        <PasswordResetFooter />
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full scale-85 flex-col items-center justify-center 2xl:scale-100">
      <ShiplexLogo color="duotoneDark" className="h-10 w-auto" />
      <h3 className="pt-10 text-black/65">{t('passwordResetSubmit.title')}</h3>
      <p className="text-typography-900/45 my-2 mb-6 w-81 text-center">
        {t('passwordResetSubmit.subtitle', { email })}
      </p>

      <VerificationCodeForm onSubmit={handleCodeSubmit} loading={loading} error={error} />

      <div className="flex w-81 justify-end">
        <Button
          variant="link"
          size="condensed"
          onClick={handleReenterEmail}
          className="text-primary hover:text-primary/75 mt-3 font-normal transition-all duration-300 hover:no-underline"
        >
          {t('passwordResetSubmit.reenter')}
        </Button>
      </div>

      <ErrorBanner error={error} className="mt-3" />
      <PasswordResetFooter />
    </div>
  );
};
